import React, { useState } from "react";
import { X } from "lucide-react";

interface DonationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (data: DonationFormData) => void;
  initialFoundation?: string;
}

interface DonationFormData {
  foundation: string;
  amount: number | null;
  name: string;
  email: string;
}

const DonationModal: React.FC<DonationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialFoundation = "GENERAL_IMPACT",
}) => {
  const [formData, setFormData] = useState<DonationFormData>({
    foundation: initialFoundation,
    amount: null,
    name: "",
    email: "",
  });

  // Update formData when initialFoundation changes and modal opens (optional, but good for re-use)
  React.useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({ ...prev, foundation: initialFoundation }));
    }
  }, [isOpen, initialFoundation]);

  const [customAmount, setCustomAmount] = useState<string>("");

  const presetAmounts = [25, 50, 100, 250, 500, 1000];

  const handleAmountSelect = (amount: number) => {
    setFormData({ ...formData, amount });
    setCustomAmount("");
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setCustomAmount(value);
    setFormData({ ...formData, amount: value ? parseInt(value) : null });
  };

  const handleSubmit = () => {
    if (onSubmit && formData.amount && formData.name && formData.email) {
      onSubmit(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-[#243677] text-white p-6 flex justify-between items-center rounded-t-lg">
          <h2 className="text-3xl font-light">Make Your Donation</h2>
          <button
            onClick={onClose}
            className="bg-[#7e9bc4] hover:bg-[#6a87b0] rounded p-2 transition-colors"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Foundation Selection */}
          <div className="mb-2">
            <h3 className="text-lg font-light text-gray-800 mb-1">
              Choose Your Foundation
            </h3>

            <div className="grid grid-cols-2 items-center gap-3">
              <label className="flex items-center px-4 py-[10px] border-[1px] border-[#2d4a7c] focus:bg-[#0B60D6]/10 active:bg-[#0B60D6]/10 rounded cursor-pointer">
                <input
                  type="radio"
                  name="foundation"
                  value="GENERAL_IMPACT"
                  checked={formData.foundation === "GENERAL_IMPACT"}
                  onChange={(e) =>
                    setFormData({ ...formData, foundation: e.target.value })
                  }
                  className="w-5 h-5 text-[#2d4a7c] focus:ring-[#2d4a7c]"
                />
                <span className="ml-3 text-gray-700">General Impact</span>
              </label>

              <label className="flex items-center px-4 py-[10px] border-[1px] border-[#BCBCBC] focus:bg-[#0B60D6]/10 active:bg-[#0B60D6]/10 rounded cursor-pointer hover:border-[#2d4a7c] transition-colors">
                <input
                  type="radio"
                  name="foundation"
                  value="KINABUTI"
                  checked={formData.foundation === "KINABUTI"}
                  onChange={(e) =>
                    setFormData({ ...formData, foundation: e.target.value })
                  }
                  className="w-5 h-5 text-[#2d4a7c] focus:ring-[#2d4a7c]"
                />
                <span className="ml-3 text-gray-700">Kinabuti Initiative</span>
              </label>
            </div>
          </div>

          {/* Amount Selection */}
          <div className="mb-2">
            <h3 className="text-lg font-light text-gray-800 mb-1">
              Select Amount
            </h3>

            <div className="grid grid-cols-3 gap-3 mb-3">
              {presetAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => handleAmountSelect(amount)}
                  className={`py-[10px] px-3 border-[1px] rounded text-[16px] transition-colors ${
                    formData.amount === amount && !customAmount
                      ? "border-[#2d4a7c] bg-[#0B60D6]/10 text-[#2d4a7c] font-medium"
                      : "border-[#BCBCBC] bg-white text-gray-700 hover:border-[#2d4a7c]"
                  }`}
                >
                  ${amount}
                </button>
              ))}
            </div>

            <input
              type="text"
              placeholder="Enter Custom Amount"
              value={customAmount}
              onChange={handleCustomAmountChange}
              className="w-full px-3 py-[10px] border-[1px] border-[#BCBCBC] rounded text-gray-700 placeholder-gray-400 focus:border-[#2d4a7c] focus:outline-none transition-colors"
            />
          </div>

          {/* Name Input */}
          <div className="mb-2">
            <label className="block text-lg font-light text-gray-800 mb-1">
              Your Name
            </label>
            <input
              type="text"
              placeholder="Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-3 py-[10px] border-[1px] border-[#BCBCBC] rounded text-gray-700 placeholder-gray-400 focus:border-[#2d4a7c] focus:outline-none transition-colors"
            />
          </div>

          {/* Email Input */}
          <div className="mb-3">
            <label className="block text-lg font-light text-gray-800 mb-1">
              Email Address
            </label>
            <input
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="w-full px-3 py-[10px] border-[1px] border-[#BCBCBC] rounded text-gray-700 placeholder-gray-400 focus:border-[#2d4a7c] focus:outline-none transition-colors"
            />
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={!formData.amount || !formData.name || !formData.email}
            className="w-full bg-[#2d4a7c] hover:bg-[#234067] text-white py-3 rounded-lg text-xl font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Donate Now
          </button>

          {/* Footer Text */}
          <p className="text-center text-gray-500 mt-6 text-sm">
            Your donation is secure and will be distributed according to your
            selection. You will receive a tax certificate at the end of the
            year.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DonationModal;
